import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X, Hash, Pin, Loader2, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import RichEditor from '../../components/admin/RichEditor';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminKnowledgeEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    body_html: '',
    body_json: null,
    excerpt: '',
    cover_image: '',
    hashtags: [],
    status: 'draft',
    pinned: false,
    meta_title: '',
    meta_description: '',
  });
  const [hashtagInput, setHashtagInput] = useState('');
  const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  const [allHashtags, setAllHashtags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const coverInputRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  // Fetch existing article when editing
  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/knowledge/articles/${id}`, { headers: authHeaders() });
        setForm({
          title: data.title || '',
          body_html: data.body_html || '',
          body_json: data.body_json || null,
          excerpt: data.excerpt || '',
          cover_image: data.cover_image || '',
          hashtags: data.hashtags || [],
          status: data.status || 'draft',
          pinned: !!data.pinned,
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
        });
      } catch (e) {
        toast.error('Failed to load article');
        navigate('/admin/knowledge');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch all known hashtags once (for autocomplete)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/knowledge/hashtags`);
        setAllHashtags(data.all_tags || []);
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/knowledge/upload-image`, fd, { headers: authHeaders() });
      setForm(f => ({ ...f, cover_image: data.url }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const onHashtagInput = (raw) => {
    setHashtagInput(raw);
    const cleaned = raw.replace(/^#/, '').toLowerCase();
    if (cleaned.length >= 1) {
      const matches = allHashtags.filter(t => t.includes(cleaned) && !form.hashtags.includes(t)).slice(0, 6);
      setHashtagSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const addHashtag = (raw) => {
    const v = (raw || '').trim().replace(/^#/, '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (!v || form.hashtags.includes(v)) { setHashtagInput(''); setShowSuggestions(false); return; }
    if (form.hashtags.length >= 10) { toast.error('Max 10 hashtags per article'); return; }
    setForm(f => ({ ...f, hashtags: [...f.hashtags, v] }));
    setHashtagInput('');
    setShowSuggestions(false);
  };

  const removeHashtag = (tag) => setForm(f => ({ ...f, hashtags: f.hashtags.filter(t => t !== tag) }));

  const onHashtagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addHashtag(hashtagInput);
    } else if (e.key === 'Backspace' && hashtagInput === '' && form.hashtags.length > 0) {
      setForm(f => ({ ...f, hashtags: f.hashtags.slice(0, -1) }));
    }
  };

  const validate = () => {
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error('Title must be at least 3 characters'); return false; }
    if (!form.body_html || form.body_html === '<p></p>') { toast.error('Article body cannot be empty'); return false; }
    return true;
  };

  const save = async (publishNow = null) => {
    if (!validate()) return;
    setSaving(true);
    const payload = { ...form };
    if (publishNow === true) payload.status = 'published';
    if (publishNow === false) payload.status = 'draft';
    try {
      if (isNew) {
        const { data } = await axios.post(`${API}/admin/knowledge/articles`, payload, { headers: authHeaders() });
        toast.success(`Article ${payload.status === 'published' ? 'published' : 'saved as draft'}`);
        navigate(`/admin/knowledge/edit/${data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/admin/knowledge/articles/${id}`, payload, { headers: authHeaders() });
        setForm(f => ({ ...f, status: payload.status }));
        toast.success(`Article ${payload.status === 'published' ? 'published' : 'updated'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-knowledge-editor">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/knowledge')} className="p-2 text-slate-400 hover:text-amber-400 transition" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-100">{isNew ? 'New Article' : 'Edit Article'}</h1>
              <p className="text-xs text-slate-500">Status: <span className="text-amber-400">{form.status}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && form.status === 'published' && (
              <a href={`/knowledge/${form.title ? '' : ''}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-amber-400 p-2" title="View live">
                <Eye className="w-4 h-4" />
              </a>
            )}
            <Button variant="outline" onClick={() => save(false)} disabled={saving} className="border-slate-700 text-slate-300" data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Draft
            </Button>
            <Button onClick={() => save(true)} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="publish-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {form.status === 'published' ? 'Update' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Main editor column */}
        <div className="space-y-5">
          {/* Cover image */}
          <div>
            <Label className="text-slate-300 mb-2 block">Cover Image</Label>
            {form.cover_image ? (
              <div className="relative rounded-xl overflow-hidden border border-slate-800 aspect-[2/1] bg-slate-900 group">
                <img src={form.cover_image} alt="cover" className="w-full h-full object-cover" />
                <button onClick={() => setForm(f => ({ ...f, cover_image: '' }))} className="absolute top-3 right-3 bg-slate-950/80 backdrop-blur p-2 rounded-full text-slate-200 hover:text-red-400" data-testid="remove-cover-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => coverInputRef.current?.click()}
                className="w-full aspect-[2/1] border-2 border-dashed border-slate-700 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-amber-500/50 hover:bg-slate-900/40 transition"
                data-testid="upload-cover-btn"
              >
                <Upload className="w-8 h-8 text-slate-500" />
                <span className="text-sm text-slate-400">Click to upload cover image (JPG / PNG / WebP, max 5MB)</span>
              </button>
            )}
            <input ref={coverInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onCoverUpload} className="hidden" data-testid="cover-input" />
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="title" className="text-slate-300 mb-2 block">Title</Label>
            <Input
              id="title"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="An attention-grabbing headline..."
              className="bg-slate-900 border-slate-700 text-slate-100 text-2xl font-semibold h-14"
              maxLength={200}
              data-testid="title-input"
            />
          </div>

          {/* Excerpt */}
          <div>
            <Label htmlFor="excerpt" className="text-slate-300 mb-2 block">Excerpt <span className="text-xs text-slate-500 font-normal">(optional — auto-generated from body if blank)</span></Label>
            <Textarea
              id="excerpt"
              value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
              placeholder="A short 1-2 line summary shown on the listing page and in search results."
              className="bg-slate-900 border-slate-700 text-slate-100 resize-none"
              rows={2}
              maxLength={400}
              data-testid="excerpt-input"
            />
          </div>

          {/* Body */}
          <div>
            <Label className="text-slate-300 mb-2 block">Body</Label>
            <RichEditor
              value={form.body_html}
              onChange={({ html, json }) => setForm(f => ({ ...f, body_html: html, body_json: json }))}
              placeholder="Start writing your article..."
            />
          </div>
        </div>

        {/* Right sidebar */}
        <aside className="space-y-5">
          {/* Pin/Feature */}
          <div className="p-4 border border-slate-800 rounded-xl bg-slate-900/40">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="pin-toggle">
              <input
                type="checkbox"
                checked={form.pinned}
                onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="flex items-center gap-1 text-sm font-medium text-slate-100"><Pin className="w-3.5 h-3.5 text-amber-400" /> Feature this article</div>
                <p className="text-xs text-slate-500 mt-1">Pinned articles appear at the top of /knowledge (max 3 active).</p>
              </div>
            </label>
          </div>

          {/* Hashtags */}
          <div className="p-4 border border-slate-800 rounded-xl bg-slate-900/40">
            <Label className="text-slate-300 mb-2 flex items-center gap-1"><Hash className="w-3.5 h-3.5" />Hashtags</Label>
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
              {form.hashtags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs px-2 py-1 rounded-full" data-testid={`hashtag-chip-${t}`}>
                  #{t}
                  <button onClick={() => removeHashtag(t)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="relative">
              <Input
                value={hashtagInput}
                onChange={e => onHashtagInput(e.target.value)}
                onKeyDown={onHashtagKeyDown}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Type and press Enter or Space"
                className="bg-slate-900 border-slate-700 text-slate-100 text-sm h-9"
                data-testid="hashtag-input"
              />
              {showSuggestions && hashtagSuggestions.length > 0 && (
                <div className="absolute z-20 top-full mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
                  {hashtagSuggestions.map(s => (
                    <button
                      key={s}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addHashtag(s)}
                      className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-amber-400 transition"
                      data-testid={`hashtag-suggestion-${s}`}
                    >
                      <Hash className="w-3 h-3 inline mr-1" />{s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">Press Enter or Space to add. Up to 10.</p>
          </div>

          {/* SEO (advanced, collapsed by default) */}
          <details className="p-4 border border-slate-800 rounded-xl bg-slate-900/40">
            <summary className="cursor-pointer text-sm font-medium text-slate-300 hover:text-amber-400">SEO (advanced — auto-generated by default)</summary>
            <div className="mt-3 space-y-3">
              <div>
                <Label htmlFor="meta_title" className="text-xs text-slate-400 mb-1 block">Meta Title</Label>
                <Input id="meta_title" value={form.meta_title} onChange={e => setForm(f => ({ ...f, meta_title: e.target.value }))} placeholder="Defaults to article title" className="bg-slate-900 border-slate-700 text-slate-100 text-sm h-9" />
              </div>
              <div>
                <Label htmlFor="meta_description" className="text-xs text-slate-400 mb-1 block">Meta Description</Label>
                <Textarea id="meta_description" value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))} placeholder="Defaults to excerpt" className="bg-slate-900 border-slate-700 text-slate-100 text-sm resize-none" rows={3} maxLength={160} />
              </div>
            </div>
          </details>
        </aside>
      </div>
    </div>
  );
};

export default AdminKnowledgeEditor;
